import type { GroupOption, PurchaseOrder } from "@shared/types";
import { LARGE_DATASET_THRESHOLD } from "@client-shared/lib/constants";
import type { PaperSize } from "@client-shared/types/pdfPrint";

/**
 * Generates a cache key for PDF caching.
 *
 * For large datasets (>= LARGE_DATASET_THRESHOLD), order IDs are sorted alphabetically for key
 * stability regardless of input order. For small datasets, orders are sorted by id then createdAt
 * to preserve chronological uniqueness.
 *
 * `stampPoNumber` changes the generated bytes, so it belongs in the key: vendor state loads
 * asynchronously, and without it a PDF cached before the preference arrived would be replayed
 * unstamped (and vice-versa after the preference is turned off).
 */
export const generateCacheKey = (
  type: string,
  orders: PurchaseOrder[],
  paperSize: PaperSize,
  groupOptions?: Set<GroupOption>,
  stampPoNumber?: boolean
): string => {
  const groupKey = groupOptions
    ? Array.from(groupOptions)
        .sort((a, b) => a.localeCompare(b))
        .join(",")
    : "";
  // Only appended when stamping, so keys for the default (unstamped) case are unchanged.
  const stampKey = stampPoNumber ? "-po" : "";

  if (orders.length >= LARGE_DATASET_THRESHOLD) {
    const orderIds = orders
      .map((order) => order.id ?? "")
      .filter((id) => id !== "")
      .sort((a, b) => a.localeCompare(b))
      .join(",");
    return `${type}-${orderIds}-${paperSize}-${groupKey}${stampKey}`;
  }

  const orderIds = orders
    .map((order) => ({ id: order.id, createdAt: order.createdAt }))
    .sort((a, b) => {
      const aId = a.id ?? "";
      const bId = b.id ?? "";
      if (aId !== bId) {
        return aId.localeCompare(bId);
      }
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    })
    .map((order) => order.id)
    .join(",");

  return `${type}-${orderIds}-${paperSize}-${groupKey}${stampKey}`;
};
